import { Button } from "@repo/ui/components/Base/Button";
import { GripVerticalIcon, TriangleIcon } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { PropsWithChildren } from "react";
import { cn } from "@repo/shared/utils";
import type { ClassNameValue } from "tailwind-merge";
import { Tooltip } from "@repo/ui/components/Base/Tooltip";

export interface AgendaComponentSortableItemProps<T> extends PropsWithChildren {
  item: T;
  onDelete?: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onMoveUp?: () => void;
  disableMoveUp?: boolean;
  onMoveDown?: () => void;
  disableMoveDown?: boolean;
  onMoveToTop?: () => void;
  className?: ClassNameValue;
  isEditable?: boolean;
  editableLabel?: string;
  notEditableLabel?: string;
  deleteLabel?: string;
  disabled?: boolean;
  renderTags?: (item: T) => React.ReactNode;
}

export const AgendaComponentSortableItem = <T extends { id: string }>({
  onMoveDown,
  onMoveUp,
  disableMoveDown,
  disableMoveUp,
  onEdit,
  onDelete,
  item,
  children,
  className,
  isEditable,
  editableLabel,
  notEditableLabel,
  deleteLabel,
  disabled,
  renderTags,
}: AgendaComponentSortableItemProps<T>) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex flex-row justify-between items-center bg-gray text-gray-foreground p-2 rounded-lg gap-1",
        className
      )}
    >
      <button type="button" {...attributes} {...listeners}>
        <GripVerticalIcon />
      </button>
      <div className="w-0 flex-grow">{children}</div>
      {renderTags?.(item)}
      {onEdit &&
        (editableLabel ? (
          <Tooltip
            asChild
            label={isEditable ? editableLabel : notEditableLabel}
            tooltipTriggerProps={{ disabled: !isEditable }}
          >
            <span tabIndex={0}>
              <Button
                disabled={!isEditable || disabled}
                variant="tertiary"
                type="button"
                size="sm"
                onClick={onEdit}
              >
                Edit
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            disabled={!isEditable || disabled}
            variant="tertiary"
            type="button"
            size="sm"
            onClick={onEdit}
          >
            Edit
          </Button>
        ))}
      {onDelete && (
        <Button
          disabled={disabled}
          tooltip={deleteLabel}
          variant="destructiveAlt"
          type="button"
          size="sm"
          onClick={onDelete}
        >
          Remove
        </Button>
      )}
      <div className="flex flex-col">
        <Button
          size="icon"
          variant="ghost"
          type="button"
          onClick={onMoveUp}
          disabled={disableMoveUp || disabled}
        >
          <TriangleIcon className="w-4 h-4 fill-white" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          type="button"
          onClick={onMoveDown}
          disabled={disableMoveDown || disabled}
        >
          <TriangleIcon className="rotate-180 h-4 w-4 fill-white" />
        </Button>
      </div>
    </div>
  );
};
